/**
 * Terms of Service Page
 * Accessible by all users (authenticated and unauthenticated)
 */

import React from 'react';
import {
  MDBContainer,
  MDBCard,
  MDBCardBody,
  MDBTypography,
  MDBIcon,
} from 'mdb-react-ui-kit';
import './TermsOfService.css';

const TermsOfService: React.FC = () => {
  return (
    <div className="terms-of-service-page">
      <MDBContainer className="py-5">
        <MDBCard className="terms-card">
          <MDBCardBody className="p-4 p-md-5">
            <div className="text-center mb-4">
              <MDBIcon fas icon="file-contract" size="3x" className="text-primary mb-3" />
              <MDBTypography tag="h1" className="h2 fw-bold">
                Terms of Service
              </MDBTypography>
              <p className="text-muted">Last updated: January 19, 2026</p>
            </div>

            <section className="terms-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                1. Acceptance of Terms
              </MDBTypography>
              <p>
                By accessing or using SolidRange ("the Service"), you agree to be bound by these Terms of Service. 
                If you do not agree to these terms, please do not use the Service.
              </p>
            </section>

            <section className="terms-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                2. Description of Service
              </MDBTypography>
              <p>
                SolidRange is an AI-powered shooting range scoring application that allows users to:
              </p>
              <ul>
                <li>Track and record shooting sessions</li>
                <li>Upload and analyze target images</li>
                <li>View performance statistics and progress</li>
                <li>Synchronize data across multiple devices</li>
              </ul>
            </section>

            <section className="terms-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                3. User Accounts
              </MDBTypography>
              <p>
                To use SolidRange, you must sign in with a Google account. You are responsible for:
              </p>
              <ul>
                <li>Maintaining the security of your account</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use</li>
              </ul>
              <p>
                Account approval is at the discretion of the administrators. We reserve the right to 
                suspend or terminate accounts that violate these terms.
              </p>
            </section>

            <section className="terms-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                4. Acceptable Use
              </MDBTypography>
              <p>You agree not to:</p>
              <ul>
                <li>Use the Service for any unlawful purpose</li>
                <li>Upload content that is harmful, offensive, or violates others' rights</li>
                <li>Attempt to gain unauthorized access to the Service or its systems</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Use automated systems or bots without permission</li>
                <li>Share your account credentials with others</li>
              </ul>
            </section>

            <section className="terms-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                5. User Content
              </MDBTypography>
              <p>
                You retain ownership of the content you upload (such as target images and session data). 
                By uploading content, you grant us a license to store, process, and display this content 
                as necessary to provide the Service.
              </p>
              <p>
                You are responsible for ensuring you have the right to upload any content and that it 
                does not violate any laws or third-party rights.
              </p>
            </section>

            <section className="terms-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                6. AI Processing
              </MDBTypography>
              <p>
                SolidRange uses artificial intelligence to analyze target images and detect shot locations. 
                While we strive for accuracy, we do not guarantee that AI detections will be 100% accurate. 
                Users should verify results and can manually adjust detected shots.
              </p>
            </section>

            <section className="terms-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                7. Service Availability
              </MDBTypography>
              <p>
                We strive to maintain the Service's availability but do not guarantee uninterrupted access. 
                The Service may be temporarily unavailable due to maintenance, updates, or circumstances 
                beyond our control.
              </p>
              <p>
                Local data storage allows you to continue using many features offline, with synchronization 
                occurring when connectivity is restored.
              </p>
            </section>

            <section className="terms-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                8. Limitation of Liability
              </MDBTypography>
              <p>
                To the maximum extent permitted by law, SolidRange and its operators shall not be liable for:
              </p>
              <ul>
                <li>Any indirect, incidental, or consequential damages</li>
                <li>Loss of data or business interruption</li>
                <li>Inaccuracies in AI-detected shot analysis</li>
                <li>Unauthorized access to your account due to your negligence</li>
              </ul>
            </section>

            <section className="terms-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                9. Disclaimer of Warranties
              </MDBTypography>
              <p>
                The Service is provided "as is" without warranties of any kind, whether express or implied, 
                including but not limited to warranties of merchantability, fitness for a particular purpose, 
                or non-infringement.
              </p>
            </section>

            <section className="terms-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                10. Modifications to the Service
              </MDBTypography>
              <p>
                We reserve the right to modify, suspend, or discontinue any part of the Service at any time. 
                We will make reasonable efforts to notify users of significant changes.
              </p>
            </section>

            <section className="terms-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                11. Changes to Terms
              </MDBTypography>
              <p>
                We may update these Terms of Service from time to time. Continued use of the Service after 
                changes constitutes acceptance of the new terms. We will notify you of material changes 
                through the application or email.
              </p>
            </section>

            <section className="terms-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                12. Governing Law
              </MDBTypography>
              <p>
                These terms shall be governed by and construed in accordance with applicable laws. 
                Any disputes arising from these terms or your use of the Service shall be resolved 
                through appropriate legal channels.
              </p>
            </section>

            <section className="terms-section">
              <MDBTypography tag="h2" className="h4 fw-bold mb-3">
                13. Contact
              </MDBTypography>
              <p>
                If you have any questions about these Terms of Service, please contact us through 
                the application's support channels.
              </p>
            </section>
          </MDBCardBody>
        </MDBCard>
      </MDBContainer>
    </div>
  );
};

export default TermsOfService;
